import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, UserPlus, Package, Building2, User, FileText, MapPin } from 'lucide-react';

import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { CURRENCY_OPTIONS } from '@/features/deals/constants';
import { Spinner } from '@/shared/components/ui/Spinner';
import { formatINR } from '@/shared/utils/currency';
import { useSalesAssigneeOptions } from '@/shared/hooks/useSalesAssignees';

import { usePipelineRecord, usePipelineStages, usePipelineMutations, stageErrorText } from '../hooks/usePipeline';
import { ProductLinesEditor, calculateAllLinesTotals, resplitLinesForSupplyType } from '../components/ProductLinesEditor';
import { useBillToPreview, type BillToAddress } from '@/features/quotations/hooks/useQuotationForm';
import { CustomerPoFields, EMPTY_CUSTOMER_PO, customerPoFromRecord, type CustomerPoValue } from '../components/CustomerPoFields';
import { ContactSelectField, type ContactSelection } from '../components/ContactSelectField';
import { staffIdentityProblem } from '@/shared/utils/staffIdentity';
import type { PipelineLine } from '../types';

/**
 * Full-page edit form for an existing pipeline record.
 * Mirrors the NewPipelineRecordPage layout — Client & Contact, Pipeline & Status,
 * Enquiry Notes & Context cards — but pre-populates all fields from the stored record
 * and submits via the update (PATCH) mutation.
 */
export function EditPipelineRecordPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: record, isLoading, isError } = usePipelineRecord(id ?? null);
  const { data: stageData } = usePipelineStages();
  const { update } = usePipelineMutations();

  const [form, setForm] = useState({
    contactId: '',
    business_name: '',
    contact_name: '',
    phone: '',
    email: '',
    clientType: '',
    source: 'direct',
    stage: 'contacted',
    assigned_to: '',
    value: '',
    currency: 'INR',
    notes: '',
    lead_disposition: '',
    disposition_reason: '',
    disposition_notes: '',
  });

  const [billingAddress, setBillingAddress] = useState<BillToAddress>({});
  const [shippingAddress, setShippingAddress] = useState<BillToAddress>({});
  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [lines, setLines] = useState<PipelineLine[]>([]);
  const [lineDiscountType, setLineDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [po, setPo] = useState<CustomerPoValue>({ ...EMPTY_CUSTOMER_PO });
  const [manualValueChanged, setManualValueChanged] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // The GST policy lives on the server (see useBillToPreview) — same call the
  // Quotation form makes. Defaults to intra-state, same as Quotations, until
  // a contact resolves otherwise.
  const { data: billToPreview } = useBillToPreview(form.contactId || null);
  const intraState = billToPreview?.intraState ?? true;

  // Re-split every line already on the record when the resolved supply type
  // changes — e.g. the record's own lines were saved wrong before this fix,
  // or the contact is swapped for one in another state during this edit.
  // Gated on `hydrated` so this doesn't fire against the pre-hydration
  // default before the record's real contactId has loaded.
  const [lastIntraState, setLastIntraState] = useState(intraState);
  useEffect(() => {
    if (!hydrated || intraState === lastIntraState) return;
    setLastIntraState(intraState);
    setLines((prev) => (prev.length ? resplitLinesForSupplyType(prev, intraState) : prev));
  }, [intraState, lastIntraState, hydrated]);

  // Populate form once the record loads (only once)
  useEffect(() => {
    if (record && !hydrated) {
      setForm({
        contactId: typeof record.contactId === 'string' ? record.contactId : (record.contactId ?? '') || '',
        business_name: record.business_name || record.name || '',
        contact_name: record.contact_name ?? '',
        // The linked contact's current details win over the record's snapshot.
        phone: (record as any).contact?.phone || record.phone || '',
        email: (record as any).contact?.email || record.email || '',
        clientType: (record as any).clientType || '',
        source: record.source ?? 'direct',
        stage: record.stage ?? 'contacted',
        assigned_to:
          typeof record.assigned_to === 'string'
            ? record.assigned_to
            : (record.assigned_to as { id?: string; _id?: string } | null)?.id
              ?? (record.assigned_to as { _id?: string } | null)?._id ?? '',
        value: record.value != null ? String(record.value) : '',
        currency: CURRENCY_OPTIONS.some((c) => c.value === record.currency) ? (record.currency as string) : 'INR',
        notes: record.notes ?? '',
        lead_disposition: (record as any).lead_disposition || '',
        disposition_reason: (record as any).disposition_reason || '',
        disposition_notes: (record as any).disposition_notes || '',
      });

      const rBill = (record as any).billingAddress || (record as any).contact?.billingAddress || {};
      const rShip = (record as any).shippingAddress || {};
      setBillingAddress(rBill);
      const hasDistinctShip = Boolean(rShip.street || rShip.city || rShip.state || rShip.zipCode) &&
        (rShip.street !== rBill.street || rShip.city !== rBill.city || rShip.state !== rBill.state || rShip.zipCode !== rBill.zipCode);
      if (hasDistinctShip) {
        setShippingAddress(rShip);
        setSameAsBilling(false);
      } else {
        setShippingAddress(rShip.street || rShip.city ? rShip : rBill);
        setSameAsBilling(!(rShip.street || rShip.city));
      }

      setLines(record.lines ?? []);
      setLineDiscountType(record.lines?.some((line) => line.discountType === 'fixed') ? 'fixed' : 'percent');
      setPo(customerPoFromRecord(record as unknown as Record<string, unknown>));
      setHydrated(true);
    }
  }, [record, hydrated]);

  useEffect(() => {
    if (billToPreview?.billTo && hydrated && sameAsBilling) {
      const b = billToPreview.billTo.billingAddress || {};
      const s = billToPreview.billTo.shippingAddress || {};
      setBillingAddress(b);
      const hasDistinctShip = Boolean(s.street || s.city || s.state || s.zipCode) &&
        (s.street !== b.street || s.city !== b.city || s.state !== b.state || s.zipCode !== b.zipCode);
      if (hasDistinctShip) {
        setShippingAddress(s);
        setSameAsBilling(false);
      } else {
        setShippingAddress(b);
      }
    }
  }, [billToPreview, hydrated, sameAsBilling]);

  // Sync expected value with product lines grand total if user hasn't overridden it manually
  useEffect(() => {
    if (lines.length > 0 && !manualValueChanged && hydrated) {
      const totals = calculateAllLinesTotals(lines);
      if (totals.grandTotal > 0) {
        setForm((f) => ({ ...f, value: String(totals.grandTotal) }));
      }
    }
  }, [lines, manualValueChanged, hydrated]);

  const applyContact = (sel: ContactSelection) =>
    setForm((f) => ({
      ...f,
      contactId: sel.contactId,
      business_name: sel.business_name || f.business_name,
      // A different client brings its OWN person/phone/email — even blank ones.
      // Keeping the previous pick's values here is how one client's (or a
      // BDM's) details ended up on another client's record.
      ...(sel.contactId && sel.contactId !== f.contactId
        ? { contact_name: sel.contact_name || '', phone: sel.phone || '', email: sel.email || '' }
        : { contact_name: sel.contact_name || f.contact_name, phone: sel.phone || f.phone, email: sel.email || f.email }),
    }));

  const set =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      if (k === 'value') setManualValueChanged(true);
      setForm((f) => ({ ...f, [k]: e.target.value }));
    };

  const stageOptions = (stageData?.stages ?? []).map((s) => ({
    value: s.key,
    label: s.label,
  }));

  // Sales only, plus whoever holds this record now — see useSalesAssignees.
  const userOptions = useSalesAssigneeOptions(form.assigned_to);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const businessName = form.business_name.trim();
    const staffProblem = staffIdentityProblem({ name: form.contact_name, email: form.email });
    if (staffProblem) {
      toast.error(staffProblem);
      return;
    }
    if (!businessName) {
      toast.error('A company / business name is required.');
      return;
    }
    if (!form.clientType) {
      toast.error('Please select Client Type (New Client or Old Client).');
      return;
    }
    if (!lines.length || lines.every((l) => !l.productId && !l.productName && !l.description)) {
      toast.error('Please add at least one product line item to save the pipeline.');
      return;
    }
    if (!id) return;

    update.mutate(
      {
        id,
        payload: {
          name: businessName,
          business_name: businessName,
          contactId: form.contactId || null,
          contact_name: form.contact_name.trim(),
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
          clientType: form.clientType,
          source: form.source,
          stage: form.stage,
          lead_disposition: form.lead_disposition || undefined,
          disposition_reason: form.disposition_reason || undefined,
          disposition_notes: form.disposition_notes.trim() || undefined,
          assigned_to: form.assigned_to || undefined,
          value: form.value ? Number(form.value) : undefined,
          currency: form.currency,
          notes: form.notes.trim() || undefined,
          billingAddress,
          shippingAddress: sameAsBilling ? billingAddress : shippingAddress,
          lines: lines.length ? lines : undefined,
          ...po,
          customerPoDate: po.customerPoDate || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Pipeline record updated successfully.');
          navigate(`/pipeline/${id}`);
        },
        onError: (err) => toast.error(stageErrorText(err, 'Could not update the record.')),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (isError || !record) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
        <p className="text-body-base text-on-surface-variant">Could not load the pipeline record.</p>
        <Button variant="outline" onClick={() => navigate('/pipeline')}>
          Back to Pipeline
        </Button>
      </div>
    );
  }

  // ── A QUOTED RECORD IS NOT EDITABLE ──
  // The detail page already hides its Edit button once a quotation has been
  // issued, but this route is reachable by a bookmark, a back button or a
  // stale tab — and PATCH /api/pipeline/:id refuses the save with a 409. Show
  // the reason here rather than letting someone retype a form that cannot be
  // submitted. See issuedQuotationForRecord in src/api-sales/order-from-pipeline.js.
  if (record.hasIssuedQuotation) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <FileText className="h-8 w-8 text-on-surface-variant" />
        <div>
          <p className="text-body-lg font-semibold">This record is read-only</p>
          <p className="mt-1 max-w-md text-body-base text-on-surface-variant">
            {record.issuedQuotationNumber || 'A quotation'} has been issued against it, so it is kept as the
            enquiry it was. To change what is being offered, revise the quotation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/pipeline/${record.id}`)}>
            Back to Record
          </Button>
          {record.issuedQuotationId && (
            <Button onClick={() => navigate(`/quotations/${record.issuedQuotationId}`)}>
              Open {record.issuedQuotationNumber || 'quotation'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex-1 flex flex-col gap-6 p-4 md:p-6 lg:p-8">
      {/* Page Header */}
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-outline/60 pb-5">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(`/pipeline/${id}`)}
            aria-label="Back to record"
            className="rounded-xl h-10 w-10 border-outline bg-surface"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-body-xs font-semibold uppercase tracking-wider text-primary">Sales Pipeline</span>
              <span className="text-body-xs text-on-surface-variant">/</span>
              <span className="text-body-xs text-on-surface-variant truncate max-w-xs">
                {record.title || record.name}
              </span>
              <span className="text-body-xs text-on-surface-variant">/</span>
              <span className="text-body-xs text-on-surface-variant">Edit</span>
            </div>
            <h1 className="flex items-center gap-2.5 text-2xl font-bold text-on-surface mt-0.5">
              <UserPlus className="h-6 w-6 text-primary" />
              Edit Pipeline Record
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate(`/pipeline/${id}`)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-pipeline-record-form"
            disabled={update.isPending}
            className="px-5 shadow-sm"
          >
            {update.isPending ? 'Saving Changes…' : 'Save Changes'}
          </Button>
        </div>
      </header>

      {/* Full Width Form */}
      <form id="edit-pipeline-record-form" onSubmit={submit} className="flex flex-col gap-6 w-full">
        {/* Top Grid: Contact Identity & Commercial Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
          {/* Card 1: Contact Information */}
          <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <User className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Client &amp; Contact</h2>
            </div>
            <div className="flex flex-col gap-3.5">
              {/* The client account is CHOSEN, never guessed — see
                  ContactSelectField. An older record may have no account
                  linked, in which case this reads as "not chosen" and can be
                  set without touching anything else. */}
              <label className="flex flex-col gap-1.5">
                <span className="text-body-sm font-medium text-on-surface">Client Account</span>
                <ContactSelectField
                  contactId={form.contactId}
                  selectedLabel={form.business_name}
                  onSelect={applyContact}
                  onClear={() => setForm((f) => ({ ...f, contactId: '' }))}
                />
              </label>

              {/* The company is the record's identity — see the same block
                  in NewPipelineRecordPage for why. */}
              <Input
                label="Company / Business Name *"
                value={form.business_name}
                onChange={set('business_name')}
                placeholder="e.g. Apex Industrial Works"
                required
              />
              <Input
                label="Contact Person"
                value={form.contact_name}
                onChange={set('contact_name')}
                placeholder="e.g. Rajesh Kumar"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Phone Number"
                  value={form.phone}
                  onChange={set('phone')}
                  inputMode="tel"
                  placeholder="+91 98765 43210"
                />
                <Input
                  label="Email Address"
                  value={form.email}
                  onChange={set('email')}
                  type="email"
                  placeholder="contact@company.com"
                />
              </div>
            </div>
          </div>

          {/* Card 2: Pipeline Classification & Commercial */}
          <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <Building2 className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Pipeline &amp; Status</h2>
            </div>
            <div className="flex flex-col gap-3.5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Select
                  label="Client Type *"
                  value={form.clientType}
                  onChange={set('clientType')}
                  required
                  options={[
                    { value: '', label: 'Select Client Type...' },
                    { value: 'New Client', label: 'New Client' },
                    { value: 'Old Client', label: 'Old Client' },
                  ]}
                />
                <Select
                  label="Lead Source"
                  value={form.source}
                  onChange={set('source')}
                  options={[
                    { value: 'direct', label: 'Direct / Walk-in' },
                    { value: 'referral', label: 'Referral' },
                    { value: 'cold_call', label: 'Cold Call' },
                    { value: 'exhibition', label: 'Exhibition / Trade Fair' },
                    { value: 'indiamart', label: 'IndiaMART' },
                    { value: 'website', label: 'Website / Inbound' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
                <Select
                  label="Stage"
                  value={form.stage}
                  onChange={set('stage')}
                  options={stageOptions.length ? stageOptions : [{ value: 'contacted', label: 'Contacted' }]}
                />
              </div>
              <Select
                label="Assigned Employee / BDM"
                value={form.assigned_to}
                onChange={set('assigned_to')}
                options={userOptions}
              />
              <div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
                  {/* Carried into the quotation raised from this record, which
                      opens billed in this currency. */}
                  <Select
                    label="Currency"
                    value={form.currency}
                    onChange={set('currency')}
                    options={CURRENCY_OPTIONS}
                  />
                  <Input
                    label={`Expected Value (${form.currency === 'INR' ? '₹' : form.currency})`}
                    value={form.value}
                    onChange={set('value')}
                    inputMode="numeric"
                    placeholder="Estimated revenue..."
                  />
                </div>
                {lines.length > 0 && (
                  <p className="mt-1 text-body-xs text-on-surface-variant">
                    💡 Auto-calculated from product lines ({formatINR(Number(form.value) || 0)}). You can also override it.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Card 3: Notes & Requirements Summary */}
          <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <FileText className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Enquiry Notes &amp; Context</h2>
            </div>
            <div className="flex flex-col flex-1 gap-2">
              <label className="flex flex-col flex-1 gap-1.5">
                <span className="text-body-sm font-medium text-on-surface">Client Requirement / Notes</span>
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  rows={6}
                  className="flex-1 w-full rounded-xl border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition"
                  placeholder="Specify client context, required industrial lubricants, current issues, target dispatch dates, or custom terms..."
                />
              </label>
            </div>
          </div>
        </div>

        {/* Section 2: Products & Requirements Line Items (Full Width) */}
        <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline/60 pb-4">
            <div>
              <h2 className="flex items-center gap-2 text-body-base font-bold text-on-surface">
                <Package className="h-5 w-5 text-primary" />
                Product Line Items &amp; Pricing (Mandatory) <span className="text-red-500">*</span>
              </h2>
              <p className="text-body-xs text-on-surface-variant mt-0.5">
                At least one product line item is required to save the sales pipeline. Select items from catalog to auto-fill MRP, base rate, unit and capacity.
              </p>
            </div>
          </div>
          <ProductLinesEditor lines={lines} onChange={setLines} intraState={intraState} currency={form.currency || 'INR'} discountType={lineDiscountType} onDiscountTypeChange={(type) => { setLineDiscountType(type); setLines((previous) => previous.map((line) => ({ ...line, discountType: type }))); }} />
        </div>

        {/* Section: Shipping / Delivery Address */}
        <div className="rounded-2xl border border-outline bg-surface p-5 shadow-sm flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline/60 pb-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Delivery Address (Ship To)</h2>
            </div>
            <label className="flex items-center gap-2 text-body-sm text-on-surface-variant cursor-pointer">
              <input
                type="checkbox"
                checked={sameAsBilling}
                onChange={(e) => {
                  const next = e.target.checked;
                  setSameAsBilling(next);
                  if (!next && !shippingAddress.street && !shippingAddress.city) {
                    setShippingAddress({
                      street: billingAddress.street || '',
                      city: billingAddress.city || '',
                      state: billingAddress.state || '',
                      zipCode: billingAddress.zipCode || '',
                      country: billingAddress.country || '',
                    });
                  }
                }}
                className="rounded border-outline text-primary focus:ring-primary"
              />
              Same as billing address
            </label>
          </div>

          {sameAsBilling ? (
            <p className="text-body-sm text-on-surface-variant py-1">
              {[
                billingAddress.street,
                [billingAddress.city, billingAddress.zipCode].filter(Boolean).join(' '),
                billingAddress.state,
                billingAddress.country,
              ].filter(Boolean).join(', ') || 'Using client account billing address (auto-populated once a client is chosen).'}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <Input
                  label="Company / Consignee Name (Ship To)"
                  value={(shippingAddress as any).companyName || (shippingAddress as any).name || ''}
                  onChange={(e) => setShippingAddress((prev: any) => ({ ...prev, companyName: e.target.value, name: e.target.value }))}
                  placeholder="e.g. Warehouse / Delivery Company Name"
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <span className="text-body-xs font-medium text-on-surface">Street / Delivery Address</span>
                <textarea
                  value={shippingAddress.street || ''}
                  onChange={(e) => setShippingAddress((prev) => ({ ...prev, street: e.target.value }))}
                  rows={2}
                  placeholder="Plot / factory / warehouse delivery address..."
                  className="w-full rounded-xl border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
              </div>
              <Input
                label="City"
                value={shippingAddress.city || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, city: e.target.value }))}
                placeholder="e.g. Pune"
              />
              <Input
                label="State"
                value={shippingAddress.state || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, state: e.target.value }))}
                placeholder="e.g. Maharashtra"
              />
              <Input
                label="Pincode / Postal Code"
                value={shippingAddress.zipCode || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, zipCode: e.target.value }))}
                placeholder="e.g. 411013"
              />
              <Input
                label="Country"
                value={shippingAddress.country || ''}
                onChange={(e) => setShippingAddress((prev) => ({ ...prev, country: e.target.value }))}
                placeholder="e.g. India"
              />
              <Input
                label="Mobile / Phone (Ship To)"
                value={(shippingAddress as any).phone || ''}
                onChange={(e) => setShippingAddress((prev: any) => ({ ...prev, phone: e.target.value }))}
                placeholder="e.g. +91 98765 43210 (optional)"
              />
              <Input
                label="Email (Ship To)"
                value={(shippingAddress as any).email || ''}
                onChange={(e) => setShippingAddress((prev: any) => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. delivery@company.com (optional)"
              />
            </div>
          )}
        </div>

        {/* What a quotation is built from — starred once this record is at (or
            being moved to) Quotation Sent. */}

        {/* Where the PO is normally captured: it arrives at the end of
            negotiation, by which point the record already exists. */}
        <CustomerPoFields value={po} onChange={setPo} />

        {/* Bottom Actions Bar */}
        <div className="flex items-center justify-end gap-3 rounded-2xl border border-outline bg-surface p-4 shadow-sm">
          <Button type="button" variant="ghost" onClick={() => navigate(`/pipeline/${id}`)}>
            Cancel
          </Button>
          <Button type="submit" disabled={update.isPending} className="px-6">
            {update.isPending ? 'Saving Changes…' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default EditPipelineRecordPage;
